<template>
  <div>
    <div class="btn-group" style="margin-bottom: 20px; justify-content: center">
      <button
        :class="[
          'btn',
          activeTab === 'reservations' ? 'btn-primary' : 'btn-outline-primary',
        ]"
        @click="activeTab = 'reservations'"
      >
        Reservations
      </button>
      <button
        class="btn btn-outline-primary"
        v-b-modal.availability-modal
      >
        Update Room Availability
      </button>
      <button
        class="btn btn-outline-primary"
        v-b-modal.pricing-modal
      >
        Update Room Pricing
      </button>
    </div>

    <div v-if="activeTab === 'reservations'">
      <h2>Reservations</h2>
      <b-form @submit.prevent="fetchReservations" class="text-center">
        <div class="d-flex justify-content-center align-items-center gap-3">
          <b-form-group label="Start Date">
            <b-form-datepicker
              v-model="reservationFilters.start_date"
            ></b-form-datepicker>
          </b-form-group>
          <b-form-group label="End Date">
            <b-form-datepicker v-model="reservationFilters.end_date"></b-form-datepicker>
          </b-form-group>
          <b-button type="submit" variant="primary">Fetch Reservations</b-button>
        </div>
      </b-form>
      <div class="mt-4">
        <b-table
          striped
          hover
          :items="reservations"
          :fields="reservationFields"
          primary-key="reservation_id"
        ></b-table>
      </div>
    </div>
    
    <b-modal
      id="availability-modal"
      title="Update Room Availability"
      @ok="updateAvailability"
      ok-title="Update"
      :ok-disabled="isUpdatingAvailability"
    >
      <b-form @submit.prevent="updateAvailability">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Start Date">
              <b-form-datepicker v-model="availabilityData.start_date"></b-form-datepicker>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="End Date">
              <b-form-datepicker v-model="availabilityData.end_date"></b-form-datepicker>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group label="Available Rooms">
          <b-form-input
            type="number"
            v-model="availabilityData.available_rooms"
            min="0"
          ></b-form-input>
        </b-form-group>
        <b-spinner v-if="isUpdatingAvailability" small class="mt-2"></b-spinner>
      </b-form>
      <b-alert v-if="availabilityError" show variant="danger" class="mt-3">
        Error updating availability. Please try again.
      </b-alert>
      <template #modal-footer>
        <b-button variant="secondary" @click="$bvModal.hide('availability-modal')">Cancel</b-button>
        <b-button variant="primary" @click="updateAvailability" :disabled="isUpdatingAvailability">
          <b-spinner v-if="isUpdatingAvailability" small></b-spinner>
          <span v-else>Update Availability</span>
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="pricing-modal"
      title="Update Room Pricing"
      @ok="updatePricing"
      ok-title="Update"
      :ok-disabled="isUpdatingPricing"
    >
      <b-form @submit.prevent="updatePricing">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Start Date">
              <b-form-datepicker v-model="pricingData.start_date"></b-form-datepicker>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="End Date">
              <b-form-datepicker v-model="pricingData.end_date"></b-form-datepicker>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group label="Price">
          <b-input-group prepend="$">
            <b-form-input
              type="number"
              v-model="pricingData.price.amount"
              min="0"
              step="0.01"
            ></b-form-input>
          </b-input-group>
        </b-form-group>
        <b-spinner v-if="isUpdatingPricing" small class="mt-2"></b-spinner>
      </b-form>
      <b-alert v-if="pricingError" show variant="danger" class="mt-3">
        Error updating pricing. Please try again.
      </b-alert>
      <template #modal-footer>
        <b-button variant="secondary" @click="$bvModal.hide('pricing-modal')">Cancel</b-button>
        <b-button variant="primary" @click="updatePricing" :disabled="isUpdatingPricing">
          <b-spinner v-if="isUpdatingPricing" small></b-spinner>
          <span v-else>Update Pricing</span>
        </b-button>
      </template>
    </b-modal>
    
    <b-toast id="update-toast" variant="success" solid auto-hide-delay="5000" toaster="b-toaster-bottom-right">
      <template #toast-header>
        <strong class="mr-auto">Status</strong>
      </template>
      {{ updateMessage }}
    </b-toast>
  </div>
</template>

<script>
import axios from "axios";

const apiClient = axios.create({
  baseURL: "https://6ea9b8e1-1958-417a-9959-44d27912cb6e.mock.pstmn.io",
  headers: {
    "Content-Type": "application/json",
  },
});

export default {
  name: "BookingComAdmin",
  data() {
    return {
      activeTab: "reservations",
      reservationFilters: {
        start_date: "2025-09-01",
        end_date: "2025-09-30",
      },
      reservations: [],
      availabilityData: {
        start_date: "2025-10-01",
        end_date: "2025-10-05",
        available_rooms: 3,
      },
      pricingData: {
        start_date: "2025-10-01",
        end_date: "2025-10-05",
        price: {
          amount: 150.0,
          currency: "USD",
        },
      },
      reservationFields: [
        { key: "reservation_id", label: "Reservation ID" },
        { key: "guest_name", label: "Guest Name" },
        { key: "room_type", label: "Room Type" },
        { key: "check_in", label: "Check-in Date" },
        { key: "check_out", label: "Check-out Date" },
        { key: "price.amount", label: "Price ($)" },
        { key: "status", label: "Status" },
      ],
      isUpdatingAvailability: false,
      isUpdatingPricing: false,
      updateMessage: "",
      availabilityError: false,
      pricingError: false,
    };
  },
  methods: {
    async fetchReservations() {
      try {
        const response = await apiClient.get(`/v1/hotels/12345/reservations`, {
          params: this.reservationFilters,
        });
        this.reservations = response.data;
      } catch (error) {
        console.error("Error fetching reservations:", error);
      }
    },
    async updateAvailability() {
      this.isUpdatingAvailability = true;
      this.availabilityError = false;
      try {
        const response = await apiClient.post(
          `/v1/hotels/12345/rooms/6789/availability`,
          this.availabilityData
        );
        this.updateMessage =
          response.data.message || "Availability updated successfully!";
        this.showToast('success', this.updateMessage);
        this.$bvModal.hide('availability-modal');
      } catch (error) {
        console.error("Error updating availability:", error);
        this.availabilityError = true;
        this.showToast('danger', 'Error updating availability. Please try again.');
      } finally {
        this.isUpdatingAvailability = false;
      }
    },
    async updatePricing() {
      this.isUpdatingPricing = true;
      this.pricingError = false;
      try {
        const response = await apiClient.post(
          `/v1/hotels/12345/rooms/6789/pricing`,
          this.pricingData
        );
        this.updateMessage = response.data.message || "Pricing updated successfully!";
        this.showToast('success', this.updateMessage);
        this.$bvModal.hide('pricing-modal');
      } catch (error) {
        console.error("Error updating pricing:", error);
        this.pricingError = true;
        this.showToast('danger', 'Error updating pricing. Please try again.');
      } finally {
        this.isUpdatingPricing = false;
      }
    },
    showToast(variant, message) {
      this.$bvToast.toast(message, {
        title: 'Status',
        variant: variant,
        solid: true,
        toaster: 'b-toaster-bottom-right'
      });
    }
  },
  mounted() {
    this.fetchReservations();
  }
};
</script>

<style scoped>
/* Existing styles */
.btn-group {
  display: flex;
  gap: 10px;
  justify-content: center;
}
.btn {
  padding: 8px 16px;
  border: 1px solid #007bff;
  background: #fff;
  color: #007bff;
  cursor: pointer;
  border-radius: 4px;
  outline: none;
  transition: background 0.2s, color 0.2s;
}
.btn-primary {
  background: #007bff;
  color: #fff;
}
.btn-outline-primary:hover {
  background: #e6f0ff;
}
.d-flex {
  display: flex;
}
.gap-3 {
  gap: 1rem;
}
</style>